import Phaser from 'phaser';
import type { AnimalKey, FruitKind, LevelConfig, ThemeKey, CharacterConfig } from '../types';
import { ANIMAL_STATS } from '../config/animals';
import { LEVELS, THEMES } from '../config/levels';
import { ensureGroundTextures, applyTheme } from '../render/themes';
import { makeMountWithRider } from '../render/mount';
import { makeFruit, fruitDisplayName } from '../render/fruits';
import { makeFence, makeLowBranch } from '../render/obstacles';
import { DEFAULT_CHARACTER } from '../config/presets';

// A self-contained two-player race scene. Each player gets their own viewport
// (top + bottom halves), own mount, own fruit count, own hearts. Obstacles are
// generated from a shared deterministic seed so both sides see the same pattern.

interface InitData {
  level: number;
  animalP1: AnimalKey;
  animalP2: AnimalKey;
}

const VIEWPORT_HEIGHT = 360;
const GROUND_OFFSET = 300; // ground line within a viewport
const GRAVITY = 1800;
const JUMP_VELOCITY_FACTOR = 1; // unused, jumps are direct from animal stats
const DUCK_TAP_DURATION_MS = 350;
const SPEED_SCALE = 0.55;

type ObstacleKind = 'fence' | 'branch' | 'fruit';
interface ObstaclePart {
  container: Phaser.GameObjects.Container;
  x: number;
  kind: ObstacleKind;
  fruitKind?: FruitKind;
  hbX: number;
  hbY: number;
  hbW: number;
  hbH: number;
  alive: boolean;
}

interface PlayerSide {
  label: string;
  animalKey: AnimalKey;
  yOffset: number; // top y for this viewport
  // World transform: this player's container holds everything visual.
  layer: Phaser.GameObjects.Container;
  groundTile: Phaser.GameObjects.TileSprite;
  hillsTile: Phaser.GameObjects.TileSprite | null;
  decoTile: Phaser.GameObjects.TileSprite | null;
  cloudGroup: Phaser.GameObjects.Group;
  mount: Phaser.GameObjects.Container;
  mountY: number; // y within layer
  velocityY: number;
  obstacles: ObstaclePart[];
  fruits: number;
  hp: number;
  invulnUntil: number;
  isDucking: boolean;
  duckPulseUntil: number;
  isHolding: boolean;
  speed: number;
  finished: boolean;
  knockedOut: boolean;
  jumpKey: Phaser.Input.Keyboard.Key;
  duckKey: Phaser.Input.Keyboard.Key;
  duckHeldAt: number;
  duckHeldEmit: boolean;
  fruitText: Phaser.GameObjects.Text;
  hearts: Phaser.GameObjects.Graphics[];
  // Touch helpers
  onJump: () => void;
  onDuckPress: () => void;
  onDuckRelease: () => void;
}

export class TwoPlayerRaceScene extends Phaser.Scene {
  private level = 1;
  private levelConfig!: LevelConfig;
  private p1!: PlayerSide;
  private p2!: PlayerSide;
  private rng!: () => number;
  private nextSpawnDistance = 800;
  private overallDistance = 0;
  private over = false;
  private overOverlay?: Phaser.GameObjects.Container;
  private paused = false;

  constructor() {
    super('TwoPlayerRaceScene');
  }

  init(data: InitData): void {
    this.level = data.level ?? 1;
    this.levelConfig = LEVELS[this.level];
    this.over = false;
    this.paused = false;
    this.overallDistance = 0;
    this.nextSpawnDistance = 800;
    // Deterministic shared spawn rng — same seed each race lets both players
    // see the same obstacle pattern.
    this.rng = mulberry32(42 + this.level * 7);
    // Players set up in create with the keys from data.
    (this as unknown as { _initData: InitData })._initData = data;
  }

  create(): void {
    ensureGroundTextures(this);
    const data = (this as unknown as { _initData: InitData })._initData;

    const W = this.scale.width;

    // Divider line between the two viewports
    this.add.rectangle(0, VIEWPORT_HEIGHT, W, 4, 0x111111).setOrigin(0, 0).setDepth(50);

    this.p1 = this.makePlayerSide('P1', data.animalP1, 0, 'jumpP1', 'duckP1');
    this.p2 = this.makePlayerSide('P2', data.animalP2, VIEWPORT_HEIGHT, 'jumpP2', 'duckP2');

    // Pause + back-to-title button bottom-right
    const pauseBtn = this.add.container(W - 24, 18).setDepth(60);
    const pauseBg = this.add
      .rectangle(0, 0, 90, 32, 0x3d2914, 0.9)
      .setStrokeStyle(2, 0xffffff)
      .setOrigin(1, 0);
    const pauseTxt = this.add
      .text(-45, 16, 'QUIT', { fontFamily: 'sans-serif', fontSize: '14px', color: '#ffffff' })
      .setOrigin(0.5);
    pauseBtn.add([pauseBg, pauseTxt]);
    pauseBg.setInteractive({ useHandCursor: true });
    pauseBg.on('pointerdown', () => this.scene.start('TitleScene'));

    // On-screen touch buttons for each player.
    this.makeTouchButtons();

    // Banner
    this.add
      .text(W / 2, 18, `Level ${this.level} — race to ${this.levelConfig.target} ${fruitDisplayName(this.levelConfig.fruit).toLowerCase()}`, {
        fontFamily: 'sans-serif',
        fontSize: '16px',
        color: '#ffffff',
        backgroundColor: '#3d2914',
        padding: { left: 8, right: 8, top: 4, bottom: 4 },
      })
      .setOrigin(0.5, 0)
      .setDepth(60);
  }

  private makePlayerSide(
    label: string,
    animalKey: AnimalKey,
    yOffset: number,
    _jumpId: string,
    _duckId: string,
  ): PlayerSide {
    const W = this.scale.width;

    // A container that holds everything visual for this side, positioned at yOffset.
    const layer = this.add.container(0, yOffset);

    // Theme parts — applyTheme draws onto the scene; we move them into the layer
    // and clip vertically using a mask.
    const themeKey: ThemeKey = this.levelConfig.theme;
    const parts = applyTheme(this, themeKey, GROUND_OFFSET);
    // Reparent everything by adding to the layer container — Phaser doesn't
    // support reparent natively, so we instead move every theme element into
    // the layer by adjusting its y. Simpler alternative: we'll just leave
    // them at their world position and use a render mask per side.
    const maskShape = this.add.rectangle(W / 2, yOffset + VIEWPORT_HEIGHT / 2, W, VIEWPORT_HEIGHT, 0x000000);
    maskShape.setVisible(false);
    const mask = maskShape.createGeometryMask();

    // Apply mask to all theme parts so they only show in this player's strip.
    [parts.groundTile, parts.hills, parts.decorations].forEach((g) => {
      if (g) g.setMask(mask);
    });
    // Move theme parts into this player's vertical band.
    parts.groundTile.y = yOffset + GROUND_OFFSET;
    if (parts.hills) parts.hills.y = yOffset + GROUND_OFFSET - 250;
    if (parts.decorations) parts.decorations.y = yOffset + GROUND_OFFSET - 124;
    parts.cloudGroup.getChildren().forEach((c) => {
      const cl = c as Phaser.GameObjects.Container;
      cl.y = yOffset + cl.y * (VIEWPORT_HEIGHT / 720); // squeeze vertically
      cl.setMask(mask);
    });

    // Sky gradient just for this side
    const sky = this.add.graphics();
    sky.fillGradientStyle(
      THEMES[themeKey].skyTop,
      THEMES[themeKey].skyTop,
      THEMES[themeKey].skyBottom,
      THEMES[themeKey].skyBottom,
      1,
    );
    sky.fillRect(0, yOffset, W, VIEWPORT_HEIGHT);
    sky.setDepth(-10);

    // Mount
    const character: CharacterConfig = DEFAULT_CHARACTER;
    const mount = makeMountWithRider(this, animalKey, character, 220, yOffset + GROUND_OFFSET - 50);
    mount.setMask(mask);

    // HUD: label + fruit + hearts
    this.add
      .text(20, yOffset + 14, label, {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffffff',
        backgroundColor: '#3d2914',
        padding: { left: 6, right: 6, top: 2, bottom: 2 },
      })
      .setDepth(55);
    const fruitText = this.add
      .text(20, yOffset + 44, `0 / ${this.levelConfig.target}`, {
        fontFamily: 'sans-serif',
        fontSize: '18px',
        color: '#a82a2a',
      })
      .setDepth(55);

    const hearts: Phaser.GameObjects.Graphics[] = [];
    for (let i = 0; i < 3; i++) {
      const g = this.add.graphics().setDepth(55);
      const x = 220 - i * 30;
      const y = yOffset + 30;
      drawHeart(g, x, y, true);
      hearts.push(g);
    }

    // Keyboard
    const kb = this.input.keyboard!;
    const jumpKey =
      label === 'P1'
        ? kb.addKey(Phaser.Input.Keyboard.KeyCodes.W)
        : kb.addKey(Phaser.Input.Keyboard.KeyCodes.UP);
    const duckKey =
      label === 'P1'
        ? kb.addKey(Phaser.Input.Keyboard.KeyCodes.S)
        : kb.addKey(Phaser.Input.Keyboard.KeyCodes.DOWN);

    const stats = ANIMAL_STATS[animalKey];
    const speed = stats.startSpeed * this.levelConfig.difficulty.speedMultiplier * SPEED_SCALE;

    const side: PlayerSide = {
      label,
      animalKey,
      yOffset,
      layer,
      groundTile: parts.groundTile,
      hillsTile: parts.hills,
      decoTile: parts.decorations,
      cloudGroup: parts.cloudGroup,
      mount,
      mountY: yOffset + GROUND_OFFSET - 50,
      velocityY: 0,
      obstacles: [],
      fruits: 0,
      hp: 3,
      invulnUntil: 0,
      isDucking: false,
      duckPulseUntil: 0,
      isHolding: false,
      speed,
      finished: false,
      knockedOut: false,
      jumpKey,
      duckKey,
      duckHeldAt: 0,
      duckHeldEmit: false,
      fruitText,
      hearts,
      onJump: () => this.tryJump(side),
      onDuckPress: () => this.duckPress(side),
      onDuckRelease: () => this.duckRelease(side),
    };

    duckKey.on('down', () => this.duckPress(side));
    duckKey.on('up', () => this.duckRelease(side));

    return side;
  }

  private makeTouchButtons(): void {
    const W = this.scale.width;

    // P1 (top half): jump = top-right of P1 strip, duck = top-left
    this.makeBtn(W - 80, 80, '▲', () => this.p1.onJump());
    this.makeBtn(W - 80, 160, '▼', undefined, () => this.p1.onDuckPress(), () => this.p1.onDuckRelease());

    // P2 (bottom half): jump = bottom-right, duck = below jump
    this.makeBtn(W - 80, VIEWPORT_HEIGHT + 80, '▲', () => this.p2.onJump());
    this.makeBtn(
      W - 80,
      VIEWPORT_HEIGHT + 160,
      '▼',
      undefined,
      () => this.p2.onDuckPress(),
      () => this.p2.onDuckRelease(),
    );
  }

  private makeBtn(
    x: number,
    y: number,
    glyph: string,
    onTap?: () => void,
    onPress?: () => void,
    onRelease?: () => void,
  ): void {
    const bg = this.add
      .circle(x, y, 32, 0x3d2914, 0.7)
      .setStrokeStyle(2, 0xffffff)
      .setDepth(70)
      .setInteractive({ useHandCursor: true });
    this.add
      .text(x, y, glyph, { fontFamily: 'sans-serif', fontSize: '28px', color: '#ffffff' })
      .setOrigin(0.5)
      .setDepth(71);
    bg.on('pointerdown', () => {
      bg.setFillStyle(0x6b3410, 0.9);
      onTap?.();
      onPress?.();
    });
    bg.on('pointerup', () => {
      bg.setFillStyle(0x3d2914, 0.7);
      onRelease?.();
    });
    bg.on('pointerout', () => {
      bg.setFillStyle(0x3d2914, 0.7);
      onRelease?.();
    });
  }

  update(_time: number, delta: number): void {
    if (this.over || this.paused) return;
    const dt = delta / 1000;

    // Edge-detect jump keys + duck-hold promotion (each side)
    [this.p1, this.p2].forEach((s) => {
      if (s.knockedOut || s.finished) return;
      if (Phaser.Input.Keyboard.JustDown(s.jumpKey)) this.tryJump(s);
      // Promote held duck after 180ms
      if (s.duckHeldAt > 0 && !s.duckHeldEmit && this.time.now - s.duckHeldAt >= 180) {
        s.duckHeldEmit = true;
        s.isHolding = true;
        this.enterDuck(s);
      }
    });

    this.overallDistance += this.p1.speed * dt; // shared distance for spawning cadence

    // Spawn shared obstacles when overallDistance reaches the next threshold.
    if (this.overallDistance >= this.nextSpawnDistance) {
      this.spawnSharedObstacle();
      const min = this.levelConfig.difficulty.spawnMinGap;
      const max = this.levelConfig.difficulty.spawnMaxGap;
      this.nextSpawnDistance += min + this.rng() * (max - min);
    }

    [this.p1, this.p2].forEach((s) => this.updateSide(s, dt));

    // Win check
    [this.p1, this.p2].forEach((s) => {
      if (!s.finished && !s.knockedOut && s.fruits >= this.levelConfig.target) {
        s.finished = true;
        this.declareWinner(s.label);
      }
    });

    // If both are knocked out, draw
    if (this.p1.knockedOut && this.p2.knockedOut && !this.over) {
      this.declareWinner('No-one — both fell!');
    }
    // If one is KO'd and the other is still going, no auto-win until target reached.
  }

  private updateSide(s: PlayerSide, dt: number): void {
    if (s.finished || s.knockedOut) return;

    // Scroll background
    s.groundTile.tilePositionX += s.speed * dt;
    if (s.hillsTile) s.hillsTile.tilePositionX += s.speed * 0.18 * dt;
    if (s.decoTile) s.decoTile.tilePositionX += s.speed * 0.55 * dt;

    // Gravity
    s.velocityY += GRAVITY * dt;
    let nextY = s.mountY + s.velocityY * dt;
    const groundLine = s.yOffset + GROUND_OFFSET - 50;
    if (nextY > groundLine) {
      nextY = groundLine;
      s.velocityY = 0;
    }
    s.mountY = nextY;
    s.mount.y = nextY;

    // End duck pulse
    if (s.isDucking && !s.isHolding && this.time.now > s.duckPulseUntil) {
      this.exitDuck(s);
    }

    // Move + check obstacles
    for (let i = s.obstacles.length - 1; i >= 0; i--) {
      const ob = s.obstacles[i];
      ob.x -= s.speed * dt;
      ob.container.x = ob.x;
      if (ob.x < -120) {
        ob.container.destroy();
        s.obstacles.splice(i, 1);
        continue;
      }
      if (!ob.alive) continue;
      // Collision check using rough bounds.
      const aLeft = 220 - 30;
      const aRight = 220 + 30;
      const aTop = s.mountY - (s.isDucking ? 22 : 40);
      const aBottom = s.mountY + 18;
      const bLeft = ob.x + ob.hbX;
      const bRight = ob.x + ob.hbX + ob.hbW;
      const bTop = s.yOffset + GROUND_OFFSET + ob.hbY;
      const bBottom = s.yOffset + GROUND_OFFSET + ob.hbY + ob.hbH;
      if (aLeft < bRight && aRight > bLeft && aTop < bBottom && aBottom > bTop) {
        this.handleHit(s, ob);
      }
    }
  }

  private spawnSharedObstacle(): void {
    const W = this.scale.width;
    const x = W + 100;
    const r = this.rng();
    const { fruitChance, duckChance } = this.levelConfig.difficulty;
    let kind: ObstacleKind;
    if (r < fruitChance) kind = 'fruit';
    else if (r < fruitChance + duckChance) kind = 'branch';
    else kind = 'fence';
    const fruitYOffset = this.rng() < 0.5 ? -40 : -110;
    [this.p1, this.p2].forEach((s) => {
      this.spawnObstacleFor(s, kind, x, fruitYOffset);
    });
  }

  private spawnObstacleFor(
    s: PlayerSide,
    kind: ObstacleKind,
    x: number,
    fruitYOffset: number,
  ): void {
    let container: Phaser.GameObjects.Container;
    let hbX = -25;
    let hbY = -28;
    let hbW = 50;
    let hbH = 56;
    let fruitKind: FruitKind | undefined;
    if (kind === 'fruit') {
      fruitKind = this.levelConfig.fruit;
      container = makeFruit(this, fruitKind, x, s.yOffset + GROUND_OFFSET + fruitYOffset);
      hbX = -18;
      hbY = fruitYOffset - 18;
      hbW = 36;
      hbH = 36;
    } else if (kind === 'branch') {
      container = makeLowBranch(this, x, s.yOffset + GROUND_OFFSET, this.levelConfig.theme);
      hbX = -40;
      hbY = -130;
      hbW = 80;
      hbH = 28;
    } else {
      container = makeFence(this, x, s.yOffset + GROUND_OFFSET, this.levelConfig.theme);
      hbX = -25;
      hbY = -58;
      hbW = 50;
      hbH = 56;
    }
    // Mask to viewport
    const mask = this.add
      .rectangle(this.scale.width / 2, s.yOffset + VIEWPORT_HEIGHT / 2, this.scale.width, VIEWPORT_HEIGHT, 0x000000)
      .setVisible(false);
    container.setMask(mask.createGeometryMask());

    s.obstacles.push({
      container,
      x,
      kind,
      fruitKind,
      hbX,
      hbY,
      hbW,
      hbH,
      alive: true,
    });
  }

  private handleHit(s: PlayerSide, ob: ObstaclePart): void {
    if (ob.kind === 'fruit') {
      s.fruits += 1;
      s.fruitText.setText(`${s.fruits} / ${this.levelConfig.target}`);
      ob.alive = false;
      ob.container.destroy();
      return;
    }
    // hostile
    if (this.time.now < s.invulnUntil) return;
    s.hp -= 1;
    s.invulnUntil = this.time.now + 900;
    this.flashMount(s);
    s.hearts.forEach((g, i) => {
      const x = 220 - i * 30;
      const y = s.yOffset + 30;
      drawHeart(g, x, y, i < s.hp);
    });
    if (s.hp <= 0) {
      s.knockedOut = true;
      s.mount.setAlpha(0.3);
    }
  }

  private flashMount(s: PlayerSide): void {
    this.tweens.add({
      targets: s.mount,
      alpha: 0.3,
      duration: 120,
      yoyo: true,
      repeat: 3,
      onComplete: () => s.mount.setAlpha(s.knockedOut ? 0.3 : 1),
    });
  }

  private tryJump(s: PlayerSide): void {
    if (s.knockedOut || s.finished) return;
    if (s.isDucking || s.isHolding) return;
    const groundLine = s.yOffset + GROUND_OFFSET - 50;
    if (s.mountY >= groundLine - 1) {
      const stats = ANIMAL_STATS[s.animalKey];
      s.velocityY = stats.jumpVelocity * JUMP_VELOCITY_FACTOR;
    }
  }

  private duckPress(s: PlayerSide): void {
    if (s.knockedOut || s.finished) return;
    s.duckHeldAt = this.time.now;
    s.duckHeldEmit = false;
  }

  private duckRelease(s: PlayerSide): void {
    if (s.duckHeldEmit) {
      // hold-end
      s.isHolding = false;
      if (s.isDucking && this.time.now > s.duckPulseUntil) this.exitDuck(s);
    } else {
      // tap → quick duck pulse
      if (!s.isHolding && !s.knockedOut && !s.finished) {
        const groundLine = s.yOffset + GROUND_OFFSET - 50;
        if (s.mountY >= groundLine - 1) {
          this.enterDuck(s);
          s.duckPulseUntil = this.time.now + DUCK_TAP_DURATION_MS;
        }
      }
    }
    s.duckHeldAt = 0;
    s.duckHeldEmit = false;
  }

  private enterDuck(s: PlayerSide): void {
    if (s.isDucking) return;
    s.isDucking = true;
    s.mount.scaleY = 0.7;
  }

  private exitDuck(s: PlayerSide): void {
    if (!s.isDucking) return;
    s.isDucking = false;
    s.mount.scaleY = 1;
  }

  private declareWinner(text: string): void {
    if (this.over) return;
    this.over = true;
    const W = this.scale.width;
    const H = this.scale.height;
    this.overOverlay = this.add.container(W / 2, H / 2).setDepth(100);
    const dim = this.add.rectangle(0, 0, W, H, 0x000000, 0.6);
    const card = this.add.rectangle(0, 0, 520, 200, 0x6b3aa8).setStrokeStyle(4, 0xffffff);
    const winLabel = text.startsWith('No-one') ? text : `${text} WINS!`;
    const title = this.add
      .text(0, -40, winLabel, {
        fontFamily: 'sans-serif',
        fontSize: '36px',
        color: '#ffffff',
      })
      .setOrigin(0.5);
    const back = this.add
      .text(0, 30, 'Tap to return to title', {
        fontFamily: 'sans-serif',
        fontSize: '20px',
        color: '#ffd9a0',
      })
      .setOrigin(0.5);
    this.overOverlay.add([dim, card, title, back]);
    this.overOverlay.setSize(W, H).setInteractive();
    this.overOverlay.on('pointerdown', () => this.scene.start('TitleScene'));
    // also any key
    this.input.keyboard?.once('keydown', () => this.scene.start('TitleScene'));
  }
}

function drawHeart(g: Phaser.GameObjects.Graphics, cx: number, cy: number, full: boolean): void {
  g.clear();
  const fill = full ? 0xc23b3b : 0x3d2914;
  const alpha = full ? 1 : 0.45;
  g.fillStyle(fill, alpha);
  g.lineStyle(2, 0x3d2914, 1);
  g.fillCircle(cx - 5, cy - 3, 6);
  g.fillCircle(cx + 5, cy - 3, 6);
  g.fillTriangle(cx - 11, cy - 1, cx + 11, cy - 1, cx, cy + 11);
}

function mulberry32(a: number): () => number {
  return () => {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

